import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, Signal, WritableSignal, inject, signal } from '@angular/core';

import { EAnalyticsGoal } from './analytics.goals';
import { AnalyticsService } from './analytics.service';
import { WINDOW } from './window.token';

export const CONSENT_STORAGE_KEY: string = 'sc_consent_v1';

export enum EConsentDecision {
    Granted = 'granted',
    Declined = 'denied',
}

/**
 * Хранилище отключается политикой браузера и режимом приватности: тип его наличия не
 * обещает, хотя объявление `Window` говорит обратное.
 */
function storageOf(windowRef: Window): Storage | undefined {
    return (windowRef as { localStorage?: Storage }).localStorage;
}

@Injectable({ providedIn: 'root' })
export class ConsentService {
    readonly #windowRef: Window = inject(WINDOW);
    readonly #platformId: object = inject(PLATFORM_ID);
    readonly #analytics: AnalyticsService = inject(AnalyticsService);

    readonly #decision: WritableSignal<EConsentDecision | null> = signal<EConsentDecision | null>(null);

    public readonly decision: Signal<EConsentDecision | null> = this.#decision.asReadonly();

    public hydrate(): void {
        if (!isPlatformBrowser(this.#platformId)) {
            return;
        }
        const stored: EConsentDecision | null = this.#readFromStorage();
        if (stored === null) {
            return;
        }
        this.#decision.set(stored);
        this.#apply(stored);
    }

    public accept(): void {
        this.#persist(EConsentDecision.Granted);
        this.#decision.set(EConsentDecision.Granted);
        this.#apply(EConsentDecision.Granted);
        this.#analytics.trackGoal(EAnalyticsGoal.ConsentAccepted);
    }

    public decline(): void {
        this.#persist(EConsentDecision.Declined);
        this.#decision.set(EConsentDecision.Declined);
        this.#apply(EConsentDecision.Declined);
        this.#analytics.trackGoal(EAnalyticsGoal.ConsentDeclined);
    }

    /**
     * Clear the stored decision so the cookie banner re-appears on next render.
     * Called from the Cookies page "manage consent" button.
     */
    public reopen(): void {
        this.#decision.set(null);
        if (!isPlatformBrowser(this.#platformId)) {
            return;
        }
        try {
            storageOf(this.#windowRef)?.removeItem(CONSENT_STORAGE_KEY);
        } catch {
            // storage disabled — in-memory state already reset, banner will reappear.
        }
    }

    #apply(decision: EConsentDecision): void {
        this.#analytics.updateConsent({ analytics_storage: decision });
    }

    #readFromStorage(): EConsentDecision | null {
        try {
            const raw: string | null = storageOf(this.#windowRef)?.getItem(CONSENT_STORAGE_KEY) ?? null;
            if (raw === EConsentDecision.Granted || raw === EConsentDecision.Declined) {
                return raw;
            }
            return null;
        } catch {
            return null;
        }
    }

    #persist(decision: EConsentDecision): void {
        try {
            storageOf(this.#windowRef)?.setItem(CONSENT_STORAGE_KEY, decision);
        } catch {
            // storage full / disabled — noop, memory-only state still works
        }
    }
}
